import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../http.service';
import { map } from 'rxjs/operators';

export interface VerificationStatus {
    email: string;
    email_verified: boolean;
    email_verified_at: string | null;
    phone_number: string;
    phone_verified: boolean;
    phone_verified_at: string | null;
    company_profile_completed: boolean;
    company_profile_completed_at: string | null;
}

export interface VerificationResponse {
    success: boolean;
    message: string;
    status?: string;
    data?: any;
}

export interface VerificationChannel {
    name: string;
    available: boolean;
}

export interface VerificationHistoryItem {
    id: number;
    recipient: string;
    channel: string;
    status: 'pending' | 'verified' | 'failed' | 'expired';
    attempts: number;
    created_at: string;
    verified_at?: string;
    expires_at?: string;
}

export interface RateLimitStatus {
    success: boolean;
    rate_limited: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class VerificationService {

    constructor(
        private httpService: HttpService
    ) { }

    /**
     * Get verification status
     */
    getStatus(): Observable<VerificationStatus> {
        return this.httpService.get('/v1/clients/verification/status').pipe(
            map(res => res.data as VerificationStatus)
        );
    }

    /**
     * Send verification code using unified channel-based endpoint
     * @param channel - 'email' or 'sms'
     * @param recipient - Email address or phone number
     */
    send(channel: 'email' | 'sms', recipient: string): Observable<VerificationResponse> {
        return this.httpService.post('/v1/clients/verification/send', { channel, recipient }).pipe(
            map(res => res as VerificationResponse)
        );
    }

    /**
     * Verify code using unified channel-based endpoint
     * @param channel - 'email' or 'sms'
     * @param recipient - Email address or phone number
     * @param code - Verification code
     */
    verify(channel: 'email' | 'sms', recipient: string, code: string): Observable<VerificationResponse> {
        return this.httpService.post('/v1/clients/verification/verify', { channel, recipient, code }).pipe(
            map(res => res as VerificationResponse)
        );
    }

    /**
     * Send email verification code (convenience method)
     */
    sendEmailVerification(email: string): Observable<VerificationResponse> {
        return this.send('email', email);
    }

    /**
     * Verify email with code (convenience method)
     */
    verifyEmail(email: string, code: string): Observable<VerificationResponse> {
        return this.verify('email', email, code);
    }

    /**
     * Send SMS verification code (convenience method)
     */
    sendSmsVerification(phone_number: string): Observable<VerificationResponse> {
        return this.send('sms', phone_number);
    }

    /**
     * Verify phone number with code (convenience method)
     */
    verifyPhone(phone_number: string, code: string): Observable<VerificationResponse> {
        return this.verify('sms', phone_number, code);
    }

    /**
     * Get verification history
     * @param recipient - Email address or phone number
     * @param channel - Optional channel filter ('email' or 'sms')
     * @param limit - Maximum number of records to return (1-50)
     */
    getHistory(recipient: string, channel?: 'email' | 'sms', limit: number = 10): Observable<VerificationHistoryItem[]> {
        const params: any = { recipient, limit };
        if (channel) {
            params.channel = channel;
        }
        return this.httpService.get('/v1/clients/verification/history', params).pipe(
            map(res => res.data as VerificationHistoryItem[])
        );
    }

    /**
     * Get available verification channels
     */
    getAvailableChannels(): Observable<VerificationChannel[]> {
        return this.httpService.get('/v1/clients/verification/channels').pipe(
            map(res => res.data as VerificationChannel[])
        );
    }

    /**
     * Check rate limit status for a recipient and channel
     * @param recipient - Email address or phone number
     * @param channel - 'email' or 'sms'
     */
    getRateLimitStatus(recipient: string, channel: 'email' | 'sms'): Observable<RateLimitStatus> {
        return this.httpService.get('/v1/clients/verification/rate-limit-status', { recipient, channel }).pipe(
            map(res => res as RateLimitStatus)
        );
    }
}
